import React, { createContext, type ReactNode, useContext } from 'react';

/**
 * Generic factory for creating type-safe data contexts.
 *
 * This pattern separates data from actions, allowing components to
 * subscribe only to what they need (reducing unnecessary re-renders).
 *
 * @example
 * ```typescript
 * const Entity = createDataContext<EntityData, EntityActions>();
 *
 * function EntityProvider({ children }) {
 *   const data = { events: [...], filters: {...} };
 *   const actions = { updateFilters: () => {...} };
 *
 *   return (
 *     <Entity.Provider data={data} actions={actions}>
 *       {children}
 *     </Entity.Provider>
 *   );
 * }
 *
 * function SomeComponent() {
 *   const { events } = Entity.useData();
 *   const { updateFilters } = Entity.useActions();
 * }
 * ```
 */

export interface DataContextResult<TData, TActions> {
  /**
   * Provider component to wrap the subtree.
   */
  Provider: (props: {
    data: TData;
    actions: TActions;
    children: ReactNode;
  }) => React.ReactElement;

  /**
   * Hook to access data. Components using this will re-render when data changes.
   */
  useData: () => TData;

  /**
   * Hook to access actions. Components using this won't re-render when data changes.
   */
  useActions: () => TActions;

  /**
   * Hook to access both data and actions (convenience).
   */
  useContext: () => { data: TData; actions: TActions };

  /**
   * Raw context (for advanced use cases like Context.Consumer).
   */
  DataContext: React.Context<TData | undefined>;
  ActionsContext: React.Context<TActions | undefined>;
}

/**
 * Create a data context with separate data and actions contexts.
 *
 * Separating data and actions allows components to:
 * - Subscribe only to data they need
 * - Use actions without triggering re-renders
 *
 * @param contextName - Name for debugging (appears in React DevTools)
 * @returns Context object with Provider and hooks
 */
export function createDataContext<TData, TActions>(
  contextName: string = 'Data'
): DataContextResult<TData, TActions> {
  const DataContext = createContext<TData | undefined>(undefined);
  const ActionsContext = createContext<TActions | undefined>(undefined);

  DataContext.displayName = `${contextName}DataContext`;
  ActionsContext.displayName = `${contextName}ActionsContext`;

  function Provider({
    data,
    actions,
    children,
  }: {
    data: TData;
    actions: TActions;
    children: ReactNode;
  }) {
    return (
      <DataContext value={data}>
        <ActionsContext value={actions}>{children}</ActionsContext>
      </DataContext>
    );
  }

  function useData(): TData {
    const context = useContext(DataContext);
    if (context === undefined) {
      throw new Error(
        `use${contextName}Data must be used within ${contextName}Provider`
      );
    }
    return context;
  }

  function useActions(): TActions {
    const context = useContext(ActionsContext);
    if (context === undefined) {
      throw new Error(
        `use${contextName}Actions must be used within ${contextName}Provider`
      );
    }
    return context;
  }

  function useFullContext(): { data: TData; actions: TActions } {
    const data = useData();
    const actions = useActions();
    return { data, actions };
  }

  return {
    Provider,
    useData,
    useActions,
    useContext: useFullContext,
    DataContext,
    ActionsContext,
  };
}

/**
 * Simpler context factory for data-only contexts (no actions).
 *
 * @example
 * ```typescript
 * const LookupContext = createSimpleContext<LookupMaps>('Lookup');
 *
 * function LookupProvider({ children }) {
 *   const lookups = useMemo(() => ({
 *     users: new Map(),
 *     objects: new Map(),
 *   }), []);
 *
 *   return (
 *     <LookupContext.Provider value={lookups}>
 *       {children}
 *     </LookupContext.Provider>
 *   );
 * }
 *
 * function Component() {
 *   const { users, objects } = LookupContext.use();
 * }
 * ```
 */
export function createSimpleContext<T>(contextName: string = 'Context') {
  const Context = createContext<T | undefined>(undefined);
  Context.displayName = contextName;

  function Provider({ value, children }: { value: T; children: ReactNode }) {
    return <Context value={value}>{children}</Context>;
  }

  function use(): T {
    const context = useContext(Context);
    if (context === undefined) {
      throw new Error(
        `use${contextName} must be used within ${contextName}Provider`
      );
    }
    return context;
  }

  return {
    Provider,
    use,
    Context,
  };
}

/**
 * Create an optional context (doesn't throw if used outside provider).
 *
 * Useful for contexts that enhance functionality but aren't required.
 *
 * @example
 * ```typescript
 * const ThemeContext = createOptionalContext<Theme>('Theme');
 *
 * function Component() {
 *   const theme = ThemeContext.use(); // Returns Theme | undefined
 *   if (theme) {
 *     // Use theme
 *   } else {
 *     // Fallback behavior
 *   }
 * }
 * ```
 */
export function createOptionalContext<T>(contextName: string = 'Context') {
  const Context = createContext<T | undefined>(undefined);
  Context.displayName = contextName;

  function Provider({ value, children }: { value: T; children: ReactNode }) {
    return <Context value={value}>{children}</Context>;
  }

  function use(): T | undefined {
    return useContext(Context);
  }

  function useOrThrow(): T {
    const context = useContext(Context);
    if (context === undefined) {
      throw new Error(
        `use${contextName}OrThrow must be used within ${contextName}Provider`
      );
    }
    return context;
  }

  return {
    Provider,
    use,
    useOrThrow,
    Context,
  };
}
